/**
 * coachEvents.ts — the one event bus between the app and the guided tour.
 *
 * Anything in the app that the tour should react to dispatches a
 * `founding:coach` CustomEvent on window (same idiom as `founding:filePanel`,
 * see ChatPage.tsx). The tour never imports page code; pages never import the
 * tour. Two kinds of names travel on the bus:
 *
 *   PLAIN events   fire EVERY time the thing happens (a build finishes, a
 *                  publish goes live, a route is entered). Markdown steps
 *                  can wait on them (`advance: event:build-finished`) and
 *                  nudges can trigger on them (`when: route-enter`).
 *   MILESTONES     fire ONCE per account, re-dispatched by
 *                  CoachMilestoneBridge after it has recorded them on the
 *                  server (`detail.milestone === true`). Nudges trigger on
 *                  them for the new-user journey ("Your app is ready").
 *
 * MILESTONES must stay byte-identical to server/lib/tourFields.js — a test
 * on each side pins the literal, because CRA cannot import across the
 * client/server boundary.
 */

import posthog from 'posthog-js';

export const COACH_EVENT = 'founding:coach';

export const MILESTONES = ['first-app', 'first-build', 'first-publish', 'journey-done'] as const;
export type MilestoneName = typeof MILESTONES[number];

export const PLAIN_EVENTS = ['route-enter', 'app-requested', 'build-finished', 'publish-live', 'plan-ready', 'template-opened'] as const;
export type PlainEventName = typeof PLAIN_EVENTS[number];

export type CoachEventName = MilestoneName | PlainEventName;

export interface CoachEventDetail {
  name: CoachEventName;
  taskId?: string;
  pathname?: string;
  url?: string | null;
  /** Set by CoachMilestoneBridge on its own re-dispatches so it never loops. */
  milestone?: boolean;
  /** Set by TemplateModal's `template-opened` dispatch. */
  templateId?: string;
  [key: string]: unknown;
}

/** Which plain event, the first time it happens, becomes which milestone. */
export const MILESTONE_FOR_EVENT: Partial<Record<PlainEventName, MilestoneName>> = {
  'app-requested': 'first-app',
  'build-finished': 'first-build',
  'publish-live': 'first-publish',
};

export function isMilestoneName(name: string): name is MilestoneName {
  return (MILESTONES as readonly string[]).includes(name);
}

export function isPlainEventName(name: string): name is PlainEventName {
  return (PLAIN_EVENTS as readonly string[]).includes(name);
}

export function dispatchCoachEvent(name: CoachEventName, detail?: Omit<CoachEventDetail, 'name'>): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent<CoachEventDetail>(COACH_EVENT, { detail: { ...(detail || {}), name } }));
  } catch {
    /* the tour must never be able to break the app */
  }
}

/** Subscribe to every coach event. Returns the unsubscribe function. */
export function onCoachEvent(handler: (detail: CoachEventDetail) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const listener = (e: Event) => {
    const detail = (e as CustomEvent<CoachEventDetail>).detail;
    if (!detail || typeof detail.name !== 'string') return;
    handler(detail);
  };
  window.addEventListener(COACH_EVENT, listener);
  return () => window.removeEventListener(COACH_EVENT, listener);
}

/**
 * PostHog capture, guarded the same way the tour always has been: no key →
 * no-op, and a throwing analytics client can never break the UI. Client-only
 * by design — see docs/guided-tours.md (no server duplicates, no Customer.io).
 */
export function trackCoach(event: string, props: Record<string, unknown>): void {
  if (!process.env.REACT_APP_POSTHOG_KEY) return;
  try {
    posthog.capture(event, props);
  } catch {
    /* analytics must never break the tour */
  }
}
